import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { quoteTranslations } from '../utils/quoteTranslations';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Calculator, Package, Plus, Trash2, ArrowDown, Ship, Loader2,
  FileCheck, XCircle, CheckCircle2, Sparkles, Anchor
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyItem = () => ({
  description: '', length: '', width: '', height: '', quantity: '1', weight_per_unit: ''
});

const QuoteCalculator = () => {
  const { language } = useLanguage();
  const t = quoteTranslations[language]?.quote || quoteTranslations.en.quote;
  const isRTL = language === 'ar';

  // Calculator state
  const [items, setItems] = useState([emptyItem()]);

  // Form state (simplified per user spec)
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    commodity: '',
    total_weight: '',
    total_cbm: '',
    origin_port: '',
    needs_customs_clearance: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Calculate CBM in real time
  const totals = useMemo(() => {
    let cbm = 0, weight = 0, packages = 0;
    items.forEach(item => {
      const l = parseFloat(item.length) || 0;
      const w = parseFloat(item.width) || 0;
      const h = parseFloat(item.height) || 0;
      const q = parseInt(item.quantity) || 0;
      const wt = parseFloat(item.weight_per_unit) || 0;
      if (l && w && h && q) {
        cbm += (l * w * h * q) / 1000000;
        packages += q;
      }
      if (wt && q) weight += wt * q;
    });
    return { cbm: cbm.toFixed(3), weight: weight.toFixed(2), packages };
  }, [items]);

  // Auto-fill CBM and weight in the form when totals change
  useEffect(() => {
    const cbmVal = parseFloat(totals.cbm);
    const weightVal = parseFloat(totals.weight);
    setForm(prev => ({
      ...prev,
      total_cbm: cbmVal > 0 ? totals.cbm : prev.total_cbm,
      total_weight: weightVal > 0 ? totals.weight : prev.total_weight
    }));
  }, [totals.cbm, totals.weight]);

  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };
  const addItem = () => setItems([...items, emptyItem()]);
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const cargo_items = items
        .filter(i => parseFloat(i.length) && parseFloat(i.width) && parseFloat(i.height))
        .map(i => ({
          description: i.description || undefined,
          length: parseFloat(i.length),
          width: parseFloat(i.width),
          height: parseFloat(i.height),
          quantity: parseInt(i.quantity) || 1,
          weight_per_unit: i.weight_per_unit ? parseFloat(i.weight_per_unit) : undefined
        }));

      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company || undefined,
        service_type: 'lcl',
        origin_port: form.origin_port,
        destination_port: t.destinationValue,
        commodity: form.commodity,
        cargo_items,
        total_cbm: parseFloat(form.total_cbm) || undefined,
        total_weight: parseFloat(form.total_weight) || undefined,
        needs_customs_clearance: form.needs_customs_clearance,
        needs_insurance: false,
        needs_door_delivery: false,
      };

      const res = await axios.post(`${BACKEND_URL}/api/quotes`, payload);
      if (res.data.success) {
        setSuccess(true);
        toast.success(t.successMessage);
        setItems([emptyItem()]);
        setForm({
          name: '', email: '', phone: '', company: '',
          commodity: '', total_weight: '', total_cbm: '',
          origin_port: '', needs_customs_clearance: true
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-24 relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background */}
      <div className="absolute inset-0 bg-[#0a1929]" />
      <div className="absolute inset-0 opacity-40" style={{
        backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,150,199,0.06) 0%, transparent 50%)'
      }} />

      <div className="relative container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            LCL Shipping
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.section_title}
          </h2>
          <p className="text-lg text-gray-300">{t.section_subtitle}</p>
        </div>

        {/* === CBM CALCULATOR === */}
        <div className="max-w-5xl mx-auto mb-8">
          <div className="rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.02] border border-white/10 backdrop-blur-sm overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <Calculator className="h-5 w-5 text-[#d4af37]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{t.calc_title}</h3>
                <p className="text-sm text-gray-400">{t.calc_subtitle}</p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {items.map((item, idx) => (
                <div key={idx} className="rounded-xl bg-white/[0.02] border border-white/10 p-4 md:p-5" data-testid={`cargo-item-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <div className="w-8 h-8 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                        <Package className="h-4 w-4 text-[#d4af37]" />
                      </div>
                      <span className="font-semibold">Package #{idx + 1}</span>
                    </div>
                    {items.length > 1 && (
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => removeItem(idx)}
                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10 h-9"
                        data-testid={`remove-item-${idx}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />{t.remove}
                      </Button>
                    )}
                  </div>

                  {/* Description - full width on all screens */}
                  <div className="mb-4">
                    <Label className="text-xs text-gray-400 mb-1.5 block">{t.description}</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      placeholder={t.description_placeholder}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37]/50 h-12"
                    />
                  </div>

                  {/* Dimensions - 3 cols mobile, 4 cols desktop */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                    <NumField label={`${t.length}`} unit={t.unit_cm} value={item.length} onChange={(v) => updateItem(idx, 'length', v)} testId={`length-${idx}`} />
                    <NumField label={`${t.width}`} unit={t.unit_cm} value={item.width} onChange={(v) => updateItem(idx, 'width', v)} testId={`width-${idx}`} />
                    <NumField label={`${t.height}`} unit={t.unit_cm} value={item.height} onChange={(v) => updateItem(idx, 'height', v)} testId={`height-${idx}`} />
                    <NumField label={t.quantity} unit="" value={item.quantity} onChange={(v) => updateItem(idx, 'quantity', v)} testId={`qty-${idx}`} />
                  </div>

                  <div className="mt-4">
                    <Label className="text-xs text-gray-400 mb-1.5 block">{t.weight_per_unit} ({t.unit_kg})</Label>
                    <Input
                      type="number" step="0.01" inputMode="decimal"
                      value={item.weight_per_unit}
                      onChange={(e) => updateItem(idx, 'weight_per_unit', e.target.value)}
                      className="max-w-xs bg-white/5 border-white/10 text-white focus:border-[#d4af37]/50 h-12"
                    />
                  </div>
                </div>
              ))}

              <Button
                type="button" onClick={addItem} variant="outline"
                className="w-full bg-transparent border-dashed border-white/20 text-gray-300 hover:bg-white/5 hover:text-white hover:border-[#d4af37]/40"
                data-testid="add-package-btn"
              >
                <Plus className="h-4 w-4 mr-2" />{t.add_package}
              </Button>
            </div>

            {/* Totals bar */}
            <div className="p-6 bg-gradient-to-r from-[#d4af37]/10 via-[#d4af37]/[0.03] to-transparent border-t border-[#d4af37]/20">
              <div className="grid grid-cols-3 gap-4">
                <TotalStat label={t.total_cbm} value={totals.cbm} unit="CBM" testId="total-cbm" />
                <TotalStat label={t.total_weight} value={totals.weight} unit={t.unit_kg} testId="total-weight" />
                <TotalStat label={t.packages} value={totals.packages} unit="" testId="total-packages" />
              </div>
              {parseFloat(totals.cbm) > 0 && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#d4af37]/90">
                  <ArrowDown className="h-4 w-4 animate-bounce" />
                  <span>{t.use_in_quote}</span>
                  <ArrowDown className="h-4 w-4 animate-bounce" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* === QUOTE FORM (SIMPLIFIED) === */}
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.02] border border-white/10 backdrop-blur-sm overflow-hidden">
            <div className="p-6 border-b border-white/10 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <Ship className="h-5 w-5 text-[#d4af37]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{t.title}</h3>
                <p className="text-sm text-gray-400">{t.subtitle}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Contact info */}
              <div className="grid md:grid-cols-2 gap-4">
                <DarkField label={t.name + ' *'} required value={form.name} onChange={(v) => setForm({...form, name: v})} testId="quote-name" />
                <DarkField label={t.email + ' *'} type="email" required value={form.email} onChange={(v) => setForm({...form, email: v})} testId="quote-email" />
                <DarkField label={t.phone + ' *'} type="tel" required value={form.phone} onChange={(v) => setForm({...form, phone: v})} testId="quote-phone" />
                <DarkField label={t.company} value={form.company} onChange={(v) => setForm({...form, company: v})} testId="quote-company" />
              </div>

              {/* Commodity */}
              <div className="pt-4 border-t border-white/10">
                <DarkField
                  label={t.commodityType + ' *'}
                  required
                  value={form.commodity}
                  onChange={(v) => setForm({...form, commodity: v})}
                  placeholder={t.commodityPlaceholder}
                  testId="quote-commodity"
                />
              </div>

              {/* Weight + Volume (auto-filled from calculator, editable) */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-300 mb-2 block">{t.weight}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.total_weight}
                    onChange={(e) => setForm({...form, total_weight: e.target.value})}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37]/50 h-12"
                    data-testid="quote-weight"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'ar' ? 'اختياري - يتم التعبئة تلقائياً عند إدخال الوزن لكل وحدة' :
                     language === 'tr' ? 'İsteğe bağlı - birim ağırlık girildiğinde otomatik doldurulur' :
                     'Optional — auto-filled when weight-per-unit is entered above'}
                  </p>
                </div>
                <DarkField
                  label={t.volume + ' *'}
                  type="number"
                  required
                  value={form.total_cbm}
                  onChange={(v) => setForm({...form, total_cbm: v})}
                  testId="quote-volume"
                />
              </div>

              {/* Origin + Destination */}
              <div className="grid md:grid-cols-2 gap-4">
                <DarkField
                  label={t.origin + ' *'}
                  required
                  value={form.origin_port}
                  onChange={(v) => setForm({...form, origin_port: v})}
                  placeholder={t.originPlaceholder}
                  testId="quote-origin"
                />
                {/* Destination is fixed to Latakia — display only */}
                <div>
                  <Label className="text-sm text-gray-300 mb-2 block">{t.destination}</Label>
                  <div className="flex items-center gap-2 px-4 h-11 rounded-md bg-[#d4af37]/5 border border-[#d4af37]/30 text-white">
                    <Anchor className="h-4 w-4 text-[#d4af37] flex-shrink-0" />
                    <span className="font-medium">{t.destinationValue}</span>
                  </div>
                </div>
              </div>

              {/* Customs Clearance Radio */}
              <div className="pt-4 border-t border-white/10">
                <Label className="text-sm text-gray-300 mb-3 block">{t.customsClearance}</Label>
                <div className="grid md:grid-cols-2 gap-3">
                  <CustomsOption
                    icon={FileCheck}
                    active={form.needs_customs_clearance === true}
                    onClick={() => setForm({...form, needs_customs_clearance: true})}
                    label={t.yes}
                    testId="customs-yes"
                  />
                  <CustomsOption
                    icon={XCircle}
                    active={form.needs_customs_clearance === false}
                    onClick={() => setForm({...form, needs_customs_clearance: false})}
                    label={t.no}
                    testId="customs-no"
                  />
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-6 bg-gradient-to-r from-[#d4af37] to-[#c9a02f] hover:from-[#e0bb42] hover:to-[#d4af37] text-[#0a1929] font-bold text-base tracking-wide"
                data-testid="quote-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-5 w-5 animate-spin mr-2" />{t.submitting}</>
                ) : (
                  <><Ship className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />{t.submit}</>
                )}
              </Button>

              {success && (
                <div className="mt-4 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-green-300 font-medium">{t.successMessage}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// -------------------- Helper Components --------------------

const NumField = ({ label, unit, value, onChange, testId }) => (
  <div>
    <Label className="text-xs text-gray-400 mb-1.5 block">
      {label}{unit && <span className="text-gray-500 ml-1">({unit})</span>}
    </Label>
    <Input
      type="number" step="0.01" inputMode="decimal" value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white/5 border-white/10 text-white focus:border-[#d4af37]/50 h-12 text-center"
      data-testid={testId}
    />
  </div>
);

const DarkField = ({ label, value, onChange, type = 'text', required, placeholder, testId }) => (
  <div>
    <Label className="text-sm text-gray-300 mb-2 block">{label}</Label>
    <Input
      type={type} required={required} value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      step={type === 'number' ? '0.01' : undefined}
      inputMode={type === 'number' ? 'decimal' : type === 'tel' ? 'tel' : type === 'email' ? 'email' : undefined}
      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#d4af37]/50 h-12"
      data-testid={testId}
    />
  </div>
);

const TotalStat = ({ label, value, unit, testId }) => (
  <div className="text-center">
    <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
    <p className="text-3xl font-bold text-white mt-1" data-testid={testId}>
      {value} <span className="text-lg text-[#d4af37] font-medium">{unit}</span>
    </p>
  </div>
);

const CustomsOption = ({ icon: Icon, active, onClick, label, testId }) => (
  <button
    type="button" onClick={onClick}
    className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
      active
        ? 'bg-[#d4af37]/10 border-[#d4af37]/50 shadow-[0_0_20px_rgba(212,175,55,0.15)]'
        : 'bg-white/[0.02] border-white/10 hover:border-white/20'
    }`}
    data-testid={testId}
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
      active ? 'bg-[#d4af37]/20' : 'bg-white/5'
    }`}>
      <Icon className={`h-5 w-5 ${active ? 'text-[#d4af37]' : 'text-gray-400'}`} />
    </div>
    <div className="flex-1">
      <p className={`font-semibold text-sm ${active ? 'text-white' : 'text-gray-300'}`}>{label}</p>
    </div>
    {active && <CheckCircle2 className="h-5 w-5 text-[#d4af37] flex-shrink-0" />}
  </button>
);

export default QuoteCalculator;
